import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Save, AlertCircle, X, Search, MapPin as LocationIcon } from 'lucide-react';
import { searchAddressesByPostcode, parseAddressForForm, formatAddressForDisplay, type UKAddress } from '../services/ukAddressService';

interface AddressData {
  buildingName?: string;
  houseNumber: string;
  street: string;
  town: string;
  postcode: string;
}

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: (data: { address: AddressData }) => void;
  onExit?: () => void;
  title: string;
  description: string;
  initialData?: {
    address?: AddressData;
  };
}

type OnboardingStep = 'address' | 'complete';

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  onComplete,
  onExit,
  title,
  description,
  initialData = {}
}) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('address');
  const [address, setAddress] = useState<AddressData>(initialData.address || {
    buildingName: '',
    houseNumber: '',
    street: '',
    town: '',
    postcode: ''
  });
  const [addressError, setAddressError] = useState('');
  
  // Postcode lookup states
  const [lookupPostcode, setLookupPostcode] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<UKAddress[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [useManualEntry, setUseManualEntry] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('address');
      // Only set address if it's not already populated (preserves user input)
      setAddress(prev => {
        const hasExistingData = prev.houseNumber || prev.street || prev.town || prev.postcode;
        if (hasExistingData) {
          return prev; // Keep user's input
        }
        return initialData.address || {
          buildingName: '',
          houseNumber: '',
          street: '',
          town: '',
          postcode: ''
        };
      });
      setAddressError('');
      
      // Fade out toolbar when modal opens
      const toolbars = document.querySelectorAll('[class*="toolbar"], [class*="fixed"], [class*="z-"]');
      toolbars.forEach(toolbar => {
        if (!toolbar.closest('[class*="modal"]') && !toolbar.closest('.fixed.inset-0')) {
          const htmlElement = toolbar as HTMLElement;
          htmlElement.style.transition = 'opacity 0.3s ease-in-out';
          htmlElement.style.opacity = '0.3';
        }
      });
    } else {
      // Restore toolbar opacity when modal closes
      const toolbars = document.querySelectorAll('[class*="toolbar"], [class*="fixed"], [class*="z-"]');
      toolbars.forEach(toolbar => {
        if (!toolbar.closest('[class*="modal"]') && !toolbar.closest('.fixed.inset-0')) {
          const htmlElement = toolbar as HTMLElement;
          htmlElement.style.opacity = '1';
        }
      });
    }
  }, [isOpen]); // Removed initialData from dependencies

  const handleAddressNext = () => {
    // Validate required fields
    if (!address.houseNumber.trim() || !address.street.trim()) {
      setAddressError('House number and street are required');
      return;
    }
    
    if (!address.town.trim()) {
      setAddressError('Town/City is required');
      return;
    }
    
    setAddressError('');
    setCurrentStep('complete');
    onComplete({ address });
  };




  const handleAddressChange = (field: keyof AddressData, value: string) => {
    setAddress(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (addressError && (value.trim().length > 0)) {
      setAddressError('');
    }
  };

  // Direct postcode search (Calendar-style pattern)
  const handlePostcodeSearch = async (postcode: string) => {
    setLookupPostcode(postcode);
    
    // Don't show results if address has already been selected (prevents re-showing dropdown)
    if (address.street && address.town && address.postcode && postcode.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      setSearchError('');
      return;
    }
    
    if (postcode.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      setSearchError('');
      return;
    }
    
    try {
      setIsSearching(true);
      setSearchError('');
      const results = await searchAddressesByPostcode(postcode);
      setSearchResults(results);
      setShowResults(results.length > 0);
    } catch (err) {
      console.error('Error searching addresses:', err);
      setSearchError('Failed to search addresses. Please try again.');
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle address selection from dropdown
  const handleAddressSelect = (selectedAddress: UKAddress) => {
    const parsed = parseAddressForForm(selectedAddress);
    setAddress(prev => ({
      ...prev,
      houseNumber: selectedAddress.house_number || '',
      street: parsed.streetAddress,
      town: parsed.town,
      postcode: parsed.postCode
    }));
    setLookupPostcode('');
    setSearchResults([]);
    setShowResults(false);
    setSearchError('');
  };

  // Toggle between lookup and manual entry
  const toggleEntryMode = () => {
    setUseManualEntry(!useManualEntry);
    setShowResults(false);
    setSearchError('');
    if (!useManualEntry) {
      // Switching to manual - clear lookup data
      setLookupPostcode('');
      setSearchResults([]);
    } else {
      // Switching to lookup - clear manual data
      setAddress({ buildingName: '', houseNumber: '', street: '', town: '', postcode: '' });
      setAddressError('');
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-600 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <MapPin size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">{title}</h2>
                  <p className="text-orange-100 text-sm">{description}</p>
                </div>
              </div>
              {onExit && (
                <button
                  onClick={onExit}
                  title="Exit to Main Menu"
                  className="p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200"
                >
                  <X size={20} className="text-white" />
                </button>
              )}
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50/50 to-white">


          {/* Step Content */}
          {currentStep === 'address' && (
            <div className="space-y-6">
              {/* Entry Mode Toggle */}
              <div className="flex items-center justify-center space-x-2 mb-4">
                <button
                  type="button"
                  onClick={toggleEntryMode}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    !useManualEntry 
                      ? 'bg-yellow-500 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Search size={14} className="inline mr-1" />
                  Postcode Lookup
                </button>
                <button
                  type="button"
                  onClick={toggleEntryMode}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    useManualEntry 
                      ? 'bg-yellow-500 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Manual Entry
                </button>
              </div>

              {!useManualEntry && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  {/* Postcode Lookup */}
                  <div className="relative">
                    <label htmlFor="lookupPostcode" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Search by Postcode
                    </label>
                    <div className="relative">
                      <input
                        id="lookupPostcode"
                        type="text"
                        value={lookupPostcode}
                        onChange={(e) => handlePostcodeSearch(e.target.value)}
                        placeholder="e.g., OL8 1LU"
                        className="w-full px-4 py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 bg-white border-gray-200 hover:border-gray-300"
                        autoFocus
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {isSearching ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                        ) : (
                          <Search size={16} className="text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Search Results Dropdown */}
                    {showResults && searchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-yellow-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {searchResults.map((addr, index) => (
                          <button
                            key={`${addr.postcode}-${index}`}
                            type="button"
                            onClick={() => handleAddressSelect(addr)}
                            className="w-full px-3 py-2 text-left hover:bg-yellow-50 border-b border-gray-100 last:border-b-0 text-sm text-gray-700 hover:text-gray-900 transition-colors duration-150"
                          >
                            <div className="flex items-center">
                              <LocationIcon size={12} className="text-gray-400 mr-2 flex-shrink-0" />
                              <div>
                                <div className="font-medium">{formatAddressForDisplay(addr)}</div>
                                <div className="text-xs text-gray-500">{addr.postcode}</div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {searchError && (
                      <div className="mt-2 flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle size={14} className="text-red-500" />
                        <span className="text-xs font-medium text-red-600">{searchError}</span>
                      </div>
                    )}
                    
                    {lookupPostcode && lookupPostcode.trim().length >= 3 && !isSearching && searchResults.length === 0 && !searchError && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <span className="text-xs text-amber-700">No addresses found for this postcode.</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {useManualEntry && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <div>
                    <label htmlFor="buildingName" className="block text-sm font-medium text-gray-700 mb-2">
                      Building / Flat Name & Number <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      id="buildingName"
                      type="text"
                      value={address.buildingName}
                      onChange={(e) => handleAddressChange('buildingName', e.target.value)}
                      placeholder="e.g., 'Flat 2 Ferndale Court'"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                      autoFocus
                    />
                    <p className="mt-1 text-xs text-gray-500">For flats, apartments, or buildings with names</p>
                  </div>
                  
                  <div>
                    <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      House Number / Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="houseNumber"
                      type="text"
                      value={address.houseNumber}
                      onChange={(e) => handleAddressChange('houseNumber', e.target.value)}
                      placeholder="e.g., 123 or 'Oak House'"
                      className={`w-full px-3 py-2 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                        addressError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                      autoFocus
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-2">
                      Street *
                    </label>
                    <input
                      id="street"
                      type="text"
                      value={address.street}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      placeholder="e.g., Main Street"
                      className={`w-full px-3 py-2 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                        addressError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddressNext()}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="town" className="block text-sm font-medium text-gray-700 mb-2">
                      Town/City *
                    </label>
                    <input
                      id="town"
                      type="text"
                      value={address.town}
                      onChange={(e) => handleAddressChange('town', e.target.value)}
                      placeholder="e.g., London"
                      className={`w-full px-3 py-2 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 ${
                        addressError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddressNext()}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="postcode" className="block text-sm font-medium text-gray-700 mb-2">
                      Postcode
                    </label>
                    <input
                      id="postcode"
                      type="text"
                      value={address.postcode}
                      onChange={(e) => handleAddressChange('postcode', e.target.value.toUpperCase())}
                      placeholder="e.g., SW1A 1AA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddressNext()}
                    />
                  </div>

                  {addressError && (
                    <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700">
                      <AlertCircle size={16} />
                      <span className="text-sm font-medium">{addressError}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Building Name and House Number for Lookup Mode */}
              {!useManualEntry && (address.street || address.town || address.postcode) && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <div>
                    <label htmlFor="buildingNameLookup" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      Building / Flat Name & Number (Optional)
                    </label>
                    <input
                      id="buildingNameLookup"
                      type="text"
                      value={address.buildingName}
                      onChange={(e) => handleAddressChange('buildingName', e.target.value)}
                      placeholder="e.g., 'Flat 2 Ferndale Court'"
                      className="w-full px-4 py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 bg-white border-gray-200 hover:border-gray-300"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">For flats, apartments, or buildings with names</p>
                  </div>
                  
                  <div>
                    <label htmlFor="houseNumberLookup" className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                      House Number / Name (Optional)
                    </label>
                    <input
                      id="houseNumberLookup"
                      type="text"
                      value={address.houseNumber}
                      onChange={(e) => handleAddressChange('houseNumber', e.target.value)}
                      placeholder="e.g., 123 or 'Oak House'"
                      className="w-full px-4 py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 bg-white border-gray-200 hover:border-gray-300"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">Add your house number or property name</p>
                  </div>
                </div>
              )}
              
              {/* Address Preview for Lookup Mode */}
              {!useManualEntry && (address.street || address.town || address.postcode) && (
                <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                  <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    Complete Address Preview
                  </h4>
                  <div className="text-sm text-green-800 space-y-1 font-medium">
                    {address.buildingName && <div>{address.buildingName}</div>}
                    {address.street && (
                      <div>
                        {address.houseNumber && `${address.houseNumber} `}{address.street}
                      </div>
                    )}
                    {address.town && <div>{address.town}</div>}
                    {address.postcode && <div>{address.postcode}</div>}
                  </div>
                </div>
              )}

              {/* Benefits info */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50/30 border border-green-200/50 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Save size={16} className="text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Auto-save Benefits</h4>
                    <ul className="text-xs text-green-700 space-y-1.5">
                      <li className="flex items-center"><span className="w-1 h-1 bg-green-500 rounded-full mr-2"></span>Your work will be automatically saved to the cloud</li>
                      <li className="flex items-center"><span className="w-1 h-1 bg-green-500 rounded-full mr-2"></span>Never lose progress due to unexpected closures</li>
                      <li className="flex items-center"><span className="w-1 h-1 bg-green-500 rounded-full mr-2"></span>Files organized by property address</li>
                      <li className="flex items-center"><span className="w-1 h-1 bg-green-500 rounded-full mr-2"></span>Access your work from any device</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-6 p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white rounded-b-2xl">
          <div className="flex-1">
            {currentStep === 'address' && onExit && (
              <button
                onClick={onExit}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
              >
                Exit to Main Menu
              </button>
            )}
          </div>
          <div className="flex-1 flex justify-end">
            {currentStep === 'address' && (
              <button
                onClick={handleAddressNext}
                className="flex items-center space-x-2 px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 ring-2 ring-yellow-200/50"
              >
                <span>Complete Setup</span>
                <Save size={16} />
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default OnboardingModal; 